import queryString from 'querystring';
import get from 'lodash/get';
import { actions, selectors } from 'app-webtech-core';
import { NOT_FOUND } from 'redux-first-router';
import { setEmbedConfig } from 'app-modules/embed/actions';
import { setDesktopConfig } from 'app-modules/desktop/actions';
import { isEnabled as enableTrending } from 'app-modules/trending/actions';
import { TYPE as EMBED_TYPE } from 'app-modules/embed/constants';
import { properties as embedSelector } from 'app-modules/embed/selectors';
import { redirect } from 'app-source/actions/redirect';
import {
  vodMovieThunk,
  vodSeriesThunk,
  vodSeriesEpisodeThunk,
  oldVodUrlsThunk,
  vodRootThunk,
} from './helpers/vod';
import { viralRouteThunk } from './helpers/viral';
import { createCookie } from '../modules/application/components/PrivacyPolicyUpdate/utils/cookie';
import { QUERY_TYPES } from './constants';
import {
  CHAT_ONLY,
  INDEX,
  TRENDING,
  TRENDING_ITEM,
  VIRAL,
  VIRAL_ITEM,
  VIRAL_CLIP,
  VIRAL_CLIP_ITEM,
  VOD,
  WATCH,
  WATCH_CHANNEL,
  DESKTOP,
  VOD_MOVIE,
  VOD_SERIES_EPISODE,
  VOD_SERIES_SEASON_EPISODE,
  VOD_SERIES,
  VOD_SERIES_SEASON,
} from './types';

const decodePath = (path) => decodeURIComponent(path);

const trendingRouteThunk = (dispatch) => {
  dispatch(enableTrending(true));
  dispatch(actions.application.appLaunchTrack(true));
};

export const routesDefinition = {
  [CHAT_ONLY]: {
    route: '/chat-live-tv',
    component: 'ChatOnly',
  },
  [DESKTOP]: {
    route: {
      path: '/desktop',
      thunk: (dispatch) =>
        dispatch(redirect({ type: WATCH, query: { type: QUERY_TYPES.DESKTOP } })),
    },
  },

  GUIDE_REDIRECT: {
    route: {
      path: '/guide',
      thunk: (dispatch) =>
        dispatch(redirect({ type: WATCH_CHANNEL, payload: { slug: 'channel-lineup' } })),
    },
  },
  [TRENDING]: {
    route: {
      path: '/trending',
      thunk: trendingRouteThunk,
    },
    component: 'Trending',
  },
  [TRENDING_ITEM]: {
    route: {
      path: '/trending/:slug',
      thunk: trendingRouteThunk,
    },
    component: 'Trending',
    fromPath: decodePath,
  },
  [VIRAL]: {
    route: {
      path: '/watch/trending',
      thunk: viralRouteThunk,
    },
    component: 'Watch',
  },
  [VIRAL_ITEM]: {
    route: {
      path: '/watch/trending/:slug',
      fromPath: decodePath,
      thunk: viralRouteThunk,
    },
    component: 'Watch',
  },
  [VIRAL_CLIP]: {
    route: {
      path: '/clip',
      fromPath: decodePath,
      thunk: viralRouteThunk,
    },
    component: 'Watch',
  },
  [VIRAL_CLIP_ITEM]: {
    route: {
      path: '/clip/:slug',
      fromPath: decodePath,
      thunk: viralRouteThunk,
    },
    component: 'Watch',
  },
  [VOD]: {
    route: {
      path: '/on-demand',
      thunk: vodRootThunk,
    },
    component: 'Vod',
  },
  [VOD_MOVIE]: {
    route: {
      path: '/on-demand/movies/:movieSlug',
      fromPath: decodePath,
      thunk: vodMovieThunk,
    },
    component: 'Vod',
  },
  [VOD_SERIES]: {
    route: {
      path: '/on-demand/series/:seriesSlug',
      fromPath: decodePath,
      thunk: vodSeriesThunk,
    },
    component: 'Vod',
  },
  [VOD_SERIES_SEASON]: {
    route: {
      path: '/on-demand/series/:seriesSlug/season/:seasonNumber',
      fromPath: decodePath,
      thunk: vodSeriesThunk,
    },
    component: 'Vod',
  },
  [VOD_SERIES_SEASON_EPISODE]: {
    route: {
      path: '/on-demand/series/:seriesSlug/season/:seasonNumber/episode/:episodeSlug',
      fromPath: decodePath,
      thunk: vodSeriesEpisodeThunk,
    },
    component: 'Vod',
  },
  [VOD_SERIES_EPISODE]: {
    route: {
      path: '/on-demand/series/:seriesSlug/episode/:episodeSlug',
      fromPath: decodePath,
      thunk: vodSeriesEpisodeThunk,
    },
    component: 'Vod',
  },
  VOD_ITEM_OLD: {
    route: {
      path: '/on-demand/:slug*',
      fromPath: decodePath,
      thunk: oldVodUrlsThunk,
    },
  },
  WATCH_TV: {
    route: {
      path: '/tv/',
      thunk: (dispatch) => dispatch(redirect({ type: WATCH })),
    },
  },
  WATCH_TV_CHANNEL: {
    route: {
      path: '/tv/:slug*',
      fromPath: decodePath,
      thunk: (dispatch, getState) => {
        const slug = get(getState(), 'location.payload.slug');
        return dispatch(redirect({ type: WATCH_CHANNEL, payload: { slug } }));
      },
    },
  },
  [WATCH]: {
    route: {
      path: '/live-tv/',
      fromPath: decodePath,
      thunk: (dispatch, getState) => {
        const state = getState();
        const locationType = get(state, 'location.type');
        const prevLocationType = get(state, 'location.prev.type');
        if (locationType === 'PAGE_WATCH' && prevLocationType === 'PAGE_WATCH_CHANNEL') {
          dispatch(redirect({ type: VOD }));
        }
      },
    },
    component: 'Watch',
    isDefault: true,
  },
  [WATCH_CHANNEL]: {
    route: {
      path: '/live-tv/:slug*',
      fromPath: decodePath,
      thunk: (dispatch, getState) => {
        const state = getState();
        const slug = get(state, 'location.payload.slug');
        const playerContentSlug = get(state, 'player.content.slug');
        if (playerContentSlug && slug && playerContentSlug !== slug) {
          const { list } = selectors.guide.properties(state);
          const channelBySlug = selectors.linearChannels.fetchBySlug(state)(slug);
          const newIndex =
            list[list.findIndex((listItem) => listItem.id === channelBySlug.id)].index;
          dispatch(actions.guide.playChannelFullscreen({ index: newIndex, setSelected: true }));
        }
      },
    },
    component: 'Watch',
  },
  [INDEX]: {
    route: {
      path: '/',
      thunk: (dispatch, getState) => {
        const embed = embedSelector(getState());

        if (embed.enabled && embed.type === EMBED_TYPE.EMBED_TYPE_VOD) {
          dispatch(redirect({ type: VOD }));
        } else {
          dispatch(redirect({ type: WATCH }));
        }
      },
    },
  },
  [NOT_FOUND]: {
    route: {
      path: '/*',
      component: 'Watch',
    },
  },
};

export const routeOptions = {
  // eslint-disable-next-line no-unused-vars
  onBeforeChange: (dispatch, getState, bag) => {
    // do work before changing the route
    const { query } = bag.action.meta;

    if (query) {
      switch (query.type) {
        case QUERY_TYPES.EMBED: {
          const embedConfig = {
            enabled: true,
            corpEmbed: false,
            type: query.embedType || EMBED_TYPE.EMBED_TYPE_LINEAR,
            disableTOSNotification: ['homepage', 'pmc'].includes(query.utm_source),
          };

          dispatch(setEmbedConfig(embedConfig));
          break;
        }
        case QUERY_TYPES.HOMEPAGE_EMBED: {
          const embedConfig = {
            enabled: false,
            corpEmbed: true,
            type: query.embedType || EMBED_TYPE.EMBED_TYPE_LINEAR,
            disableTOSNotification: ['homepage', 'pmc'].includes(query.utm_source),
          };

          dispatch(setEmbedConfig(embedConfig));
          break;
        }
        case QUERY_TYPES.DESKTOP: {
          dispatch(setDesktopConfig());
          break;
        }
        default:
        // do nothing
      }

      if (query.setTOS) {
        createCookie(false).then(() => global.window.close());
      }
    }
  },
  // eslint-disable-next-line no-unused-vars
  onAfterChange: (dispatch, getState) => {
    // do work after changing the route
  },
  querySerializer: queryString,
};
